import {
  PredefinedFieldKey,
  type FormFieldGroup,
  type FormFragment,
} from '@seek/cmsu-cms-connect';

import type { AutofillableValue, DynamicFormValues, FormField } from './types';
/**
 * Creates initial form values object from form fields
 * Handles different field types and their specific initialization requirements
 */
export const createFormValues = (
  fields: FormField[],
  autofillableValue: AutofillableValue,
): DynamicFormValues => {
  const formValues: DynamicFormValues = {};

  /**
   * Processes a single form field and adds its initial value to formValues
   * Handles different field types with their specific initialization logic
   */
  const processField = (field: FormField) => {
    const fieldKey = getValidFieldKey(field);

    switch (field.__typename) {
      case 'FormCheckboxGroup':
        // Initialize checkbox group with all options set to false
        // Each option becomes a separate field in the form values
        formValues[fieldKey] = field.options.reduce(
          (
            checkboxValues: Record<string, boolean>,
            option: { name: string | number },
          ) => ({
            ...checkboxValues,
            [option.name]: false,
          }),
          {},
        );
        break;

      case 'FormHiddenFieldGroup':
        // Hidden fields are flattened into the main form values object
        // Each key-value pair from the hidden field group becomes a top-level field
        const hiddenFieldValues = field.keyValues.reduce(
          (
            hiddenValues: Record<string, any>,
            keyValue: { key: string; value: any },
          ) => ({
            ...hiddenValues,
            [keyValue.key]: keyValue.value,
          }),
          {},
        );
        Object.assign(formValues, hiddenFieldValues);
        break;

      case 'FormFieldGroup':
        // Recursively process nested fields within the field group
        // This handles both visible and hidden fields for complete form state initialization
        const fieldGroup = field as FormFieldGroup;
        fieldGroup.fields?.forEach((nestedField) =>
          processField(nestedField as FormField),
        );
        break;

      case 'FormInputText':
        // if no autofillableValue, we don't need to autofill
        if (!field.autofillableValue) {
          formValues[fieldKey] = '';
          break;
        }

        formValues[fieldKey] =
          (field.autofillableValue &&
            autofillableValue[
              field.autofillableValue as keyof AutofillableValue
            ]) ||
          '';
        break;

      default:
        // For all other field types (text, email, number, textarea, select, radio)
        // Initialize with empty string as default value
        formValues[fieldKey] = '';
        break;
    }
  };

  // Process all fields to build the complete form values object
  (fields ?? []).forEach(processField);
  return formValues;
};

/**
 * Adds actualKey property to form fields for easier field identification
 * The actualKey is the resolved field identifier from predefinedKey, key, or label
 */
export const createFieldActualKey = (form: FormFragment) => {
  /**
   * Processes a single field and adds actualKey property
   * Handles nested fields within FormFieldGroup recursively
   */
  const addActualKeyToField = (field: FormField): FormField => {
    // FormHiddenFieldGroup doesn't need actualKey as it's flattened into form values
    if (field.__typename === 'FormHiddenFieldGroup') {
      return field;
    }

    // Add actualKey to the field for easier identification
    const fieldWithKey = {
      ...field,
      actualKey: getValidFieldKey(field),
    };

    // Handle FormFieldGroup by recursively processing its nested fields
    if (field.__typename === 'FormFieldGroup') {
      const fieldGroup = field as FormFieldGroup;
      return {
        ...fieldWithKey,
        fields:
          fieldGroup.fields?.map((nestedField) =>
            addActualKeyToField(nestedField as FormField),
          ) || [],
      } as FormField;
    }

    return fieldWithKey;
  };

  // Process all form fields and return the enhanced form structure
  return (form.fields ?? []).map(addActualKeyToField) || [];
};

/** All fields are somewhat valid because its going to fallback to the label because label is guaranteed to be present (required) */
export const getValidFieldKey = (field: FormField): string => {
  if (field.__typename === 'FormHiddenFieldGroup') {
    return '';
  }

  // FormFieldGroup doesn't have predefinedKey or key, so skip it
  if (field.__typename === 'FormFieldGroup') {
    return '';
  }

  // Check if field has predefinedKey property
  if (
    field.predefinedKey &&
    field.predefinedKey !== PredefinedFieldKey.Custom
  ) {
    return field.predefinedKey;
  }

  // Check if field has key property
  if ('key' in field && field.key) {
    return field.key;
  }
  // checking if no predefined key or key is present
  // if so, we fallback to the label
  // currently different __typename has different label keys
  if (
    field.__typename === 'FormInputText' ||
    field.__typename === 'FormInputEmail' ||
    field.__typename === 'FormInputNumber'
  ) {
    return toSimpleCamelCase(field.inputLabel);
  }

  if (field.__typename === 'FormTextarea') {
    return toSimpleCamelCase(field.textareaLabel);
  }

  if (
    field.__typename === 'FormRadioGroup' ||
    field.__typename === 'FormCheckboxGroup'
  ) {
    return toSimpleCamelCase(field.label);
  }

  if (field.__typename === 'FormSelect') {
    return toSimpleCamelCase(field.selectLabel);
  }

  throw new Error('Invalid field configuration - no valid key found');
};

/**
 * Simple camel case, usually source is a string with space ('form label')
 * @param str
 * @returns
 */
export const toSimpleCamelCase = (str: string) => {
  const camelCase = str.replace(/[- ]+([a-zA-Z])/g, (_, letter) =>
    letter.toUpperCase(),
  );
  return camelCase.charAt(0).toLowerCase() + camelCase.slice(1);
};
